import React, { forwardRef } from "react";
import { Box, Typography } from "@mui/material";
import { TextInputContainer, TextInputField, Label } from "./TextInput.styled";

const MULTILINE_VERTICAL_PADDING = 24;
const MULTILINE_LINE_HEIGHT_RATIO = 1.5;

const resolveFontSizePx = (fontSize: string | number): number => {
  if (typeof fontSize === "number") {
    return fontSize;
  }

  return parseFloat(fontSize) || 16;
};

const resolveMultilineContainerHeight = (
  height: string | number,
  rows: number | undefined,
  fontSize: string | number,
): string | number => {
  if (height !== "auto") {
    return height;
  }

  if (!rows) {
    return "auto";
  }

  const lineHeight = resolveFontSizePx(fontSize) * MULTILINE_LINE_HEIGHT_RATIO;

  return rows * lineHeight + MULTILINE_VERTICAL_PADDING;
};

interface TextInputProps {
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onFocus?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onBlur?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  disabled?: boolean;
  readOnly?: boolean;
  fullWidth?: boolean;
  width?: string | number;
  fontSize?: string | number;
  type?: string;
  padding?: string | number;
  name?: string;
  id?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  height?: string | number;
  maxLength?: number;
  autoWidth?: boolean;
  minWidth?: string | number;
}

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      label,
      placeholder,
      value,
      onChange,
      onFocus,
      onBlur,
      disabled = false,
      readOnly = false,
      fullWidth = true,
      fontSize = "16px",
      width,
      padding = "0 16px",
      type = "text",
      name,
      id,
      error = false,
      helperText,
      required = false,
      multiline = false,
      rows,
      height = "auto",
      maxLength,
      autoWidth = false,
      minWidth = 60,
    },
    ref,
  ) => {
    const resolvedMinWidth =
      typeof minWidth === "number" ? minWidth : parseInt(String(minWidth), 10) || 60;
    const contentLength = Math.max(value?.length ?? 0, placeholder?.length ?? 0, 1);
    const autoResolvedWidth = Math.max(
      resolvedMinWidth,
      Math.ceil(contentLength * 8.5) + 28,
    );
    const containerWidth = fullWidth
      ? "100%"
      : autoWidth
        ? autoResolvedWidth
        : width;
    const containerHeight = multiline
      ? resolveMultilineContainerHeight(height, rows, fontSize)
      : height === "auto"
        ? "48px"
        : height;

    return (
      <Box sx={{ width: containerWidth }}>
        {label && (
          <Label>
            {label}
            {required && "*"}
          </Label>
        )}
        <TextInputContainer
          sx={{ height: containerHeight }}
          padding={padding}
          error={error || undefined}
          multiline={multiline}
          readOnly={readOnly}
        >
          <TextInputField
            ref={ref}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            disabled={disabled}
            readOnly={readOnly}
            fullWidth
            fontSize={fontSize}
            multiline={multiline}
            type={type}
            name={name}
            id={id}
            rows={rows}
            inputProps={{
              maxLength,
              readOnly,
              ...(rows ? { rows } : {}),
            }}
          />
        </TextInputContainer>
        {helperText && (
          <Typography
            variant="caption"
            sx={{
              color: error ? "status.error" : "text.secondary",
              fontSize: "12px",
              mt: 0.5,
              display: "block",
            }}
          >
            {helperText}
          </Typography>
        )}
      </Box>
    );
  },
);

TextInput.displayName = "TextInput";

export default TextInput;
